import { Injectable, inject, signal, computed } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { ErrorHandlerService } from './error-handler.service';
import { OrganizationContextService } from './organization-context.service';
import { SubscriptionService } from './subscription.service';
import {
  FeatureFlag,
  FeatureCode,
  FEATURE_CODES,
  FEATURE_REGISTRY,
  FeatureSettings,
  InventoryFeatureSettings,
  DEFAULT_INVENTORY_SETTINGS,
  DiscountsFeatureSettings,
  DEFAULT_DISCOUNTS_SETTINGS,
  DeliveryFeatureSettings,
  DEFAULT_DELIVERY_SETTINGS,
  ShiftFeatureSettings,
  DEFAULT_SHIFT_SETTINGS,
  TimeAttendanceFeatureSettings,
  DEFAULT_TIME_ATTENDANCE_SETTINGS,
} from '../model/feature-flag.model';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private supabase = inject(SupabaseService);
  private errorHandler = inject(ErrorHandlerService);
  private orgContext = inject(OrganizationContextService);
  private subscriptionService = inject(SubscriptionService);

  // Cached feature flags for current organization
  private _featureFlags = signal<FeatureFlag[]>([]);
  private _loading = signal(false);
  private _loaded = signal(false);

  // Public signals
  readonly featureFlags = this._featureFlags.asReadonly();
  readonly loading = this._loading.asReadonly();
  /**
   * True once `loadFeatureFlags()` has successfully fetched the current org's
   * rows. Read by `FeatureEnabledGuard` so a direct URL / hard refresh does not
   * evaluate `isEnabled()` against an empty signal. Reset by `clearState()` on
   * org switch.
   */
  readonly loaded = this._loaded.asReadonly();

  // ============================================================================
  // Generic accessors — single source of truth for "is this feature on?"
  // ============================================================================
  // Adding a new feature flag = adding ONE entry to FEATURE_REGISTRY in
  // feature-flag.model.ts. The methods below read from that registry, so the
  // per-feature computed signals further down are pure delegates kept only
  // for backward compatibility with existing callers.

  /**
   * True if a feature flag is currently enabled for the active organization.
   * Combines tier availability (from subscription) with the org-level toggle,
   * unless the registry marks the feature `alwaysAvailable` (in which case
   * only the org-level toggle matters).
   *
   * With no row for the org, the registry's `defaultEnabled` decides. Signup
   * writes no flag rows, so that value is what every new org starts with.
   */
  isEnabled(code: FeatureCode): boolean {
    const entry = FEATURE_REGISTRY.find((f) => f.code === code);
    const flag = this._featureFlags().find((f) => f.feature_code === code);
    const flagOn = flag !== undefined ? flag.is_enabled : (entry?.defaultEnabled ?? false);
    if (entry?.alwaysAvailable) return flagOn;
    return this.subscriptionService.hasFeature(code) && flagOn;
  }

  /** True if the active org's tier includes this feature (or it's `alwaysAvailable`). */
  isAvailable(code: FeatureCode): boolean {
    const entry = FEATURE_REGISTRY.find((f) => f.code === code);
    if (entry?.alwaysAvailable) return true;
    return this.subscriptionService.hasFeature(code);
  }

  /** True if the active org's tier does NOT include this feature. */
  isPaywalled(code: FeatureCode): boolean {
    return !this.isAvailable(code);
  }

  // Computed: Get inventory settings
  readonly inventorySettings = computed((): InventoryFeatureSettings => {
    const flags = this._featureFlags();
    const inventoryFlag = flags.find((f) => f.feature_code === FEATURE_CODES.INVENTORY);
    if (inventoryFlag?.settings) {
      return inventoryFlag.settings as InventoryFeatureSettings;
    }
    return DEFAULT_INVENTORY_SETTINGS;
  });

  // Computed: Get discounts settings
  readonly discountsSettings = computed((): DiscountsFeatureSettings => {
    const flags = this._featureFlags();
    const discountsFlag = flags.find((f) => f.feature_code === FEATURE_CODES.DISCOUNTS);
    if (discountsFlag?.settings && 'manual_discounts_enabled' in discountsFlag.settings) {
      return discountsFlag.settings as DiscountsFeatureSettings;
    }
    return DEFAULT_DISCOUNTS_SETTINGS;
  });

  // Computed: Get delivery settings
  readonly deliverySettings = computed((): DeliveryFeatureSettings => {
    const flags = this._featureFlags();
    const deliveryFlag = flags.find((f) => f.feature_code === FEATURE_CODES.DELIVERY);
    if (deliveryFlag?.settings && 'payment_number' in deliveryFlag.settings) {
      return deliveryFlag.settings as DeliveryFeatureSettings;
    }
    return DEFAULT_DELIVERY_SETTINGS;
  });

  // Computed: Get cash-shift management settings
  readonly shiftSettings = computed((): ShiftFeatureSettings => {
    const flags = this._featureFlags();
    const shiftFlag = flags.find((f) => f.feature_code === FEATURE_CODES.SHIFTS);
    if (shiftFlag?.settings && 'enforcement_mode' in shiftFlag.settings) {
      return shiftFlag.settings as ShiftFeatureSettings;
    }
    return DEFAULT_SHIFT_SETTINGS;
  });

  // Computed: Get time and attendance settings
  readonly timeAttendanceSettings = computed((): TimeAttendanceFeatureSettings => {
    const flags = this._featureFlags();
    const flag = flags.find((f) => f.feature_code === FEATURE_CODES.TIME_ATTENDANCE);
    if (flag?.settings && typeof flag.settings === 'object') {
      return flag.settings as TimeAttendanceFeatureSettings;
    }
    return DEFAULT_TIME_ATTENDANCE_SETTINGS;
  });

  // ============================================================================
  // Backward-compat computeds — thin delegates to isEnabled/isPaywalled/isAvailable.
  // Kept so existing callers across the app keep working without changes.
  // New code should call isEnabled('xxx') / isPaywalled('xxx') directly.
  // ============================================================================

  readonly timeAttendanceEnabled = computed(() => this.isEnabled('time_attendance'));
  readonly timeAttendanceAvailable = computed(() => this.isAvailable('time_attendance'));
  readonly timeAttendancePaywalled = computed(() => this.isPaywalled('time_attendance'));

  readonly posOrderingEnabled = computed(() => this.isEnabled('pos_ordering'));
  readonly posOrderingAvailable = computed(() => this.isAvailable('pos_ordering'));

  readonly inventoryEnabled = computed(() => this.isEnabled('inventory'));
  readonly inventoryAvailable = computed(() => this.isAvailable('inventory'));
  readonly inventoryPaywalled = computed(() => this.isPaywalled('inventory'));

  readonly shiftsEnabled = computed(() => this.isEnabled('shifts'));
  readonly shiftsAvailable = computed(() => this.isAvailable('shifts'));
  readonly shiftsPaywalled = computed(() => this.isPaywalled('shifts'));

  readonly discountsEnabled = computed(() => this.isEnabled('discounts'));
  readonly discountsAvailable = computed(() => this.isAvailable('discounts'));
  readonly discountsPaywalled = computed(() => this.isPaywalled('discounts'));

  // Manual discounts — sub-setting of the discounts feature, not a top-level flag.
  readonly manualDiscountsEnabled = computed(() => {
    if (!this.discountsEnabled()) return false;
    return this.discountsSettings().manual_discounts_enabled === true;
  });

  readonly kitchenStationsEnabled = computed(() => this.isEnabled('kitchen_stations'));
  readonly kitchenStationsAvailable = computed(() => this.isAvailable('kitchen_stations'));
  readonly kitchenStationsPaywalled = computed(() => this.isPaywalled('kitchen_stations'));

  readonly emailReceiptsEnabled = computed(() => this.isEnabled('email_receipts'));
  readonly emailReceiptsAvailable = computed(() => this.isAvailable('email_receipts'));
  readonly emailReceiptsPaywalled = computed(() => this.isPaywalled('email_receipts'));

  readonly orderStatusSmsEnabled = computed(() => this.isEnabled('order_status_sms'));
  readonly orderStatusSmsAvailable = computed(() => this.isAvailable('order_status_sms'));
  readonly orderStatusSmsPaywalled = computed(() => this.isPaywalled('order_status_sms'));

  readonly estimatesEnabled = computed(() => this.isEnabled('estimates'));
  readonly estimatesAvailable = computed(() => this.isAvailable('estimates'));
  readonly estimatesPaywalled = computed(() => this.isPaywalled('estimates'));

  readonly branchProductsEnabled = computed(() => this.isEnabled('branch_products'));
  readonly branchProductsAvailable = computed(() => this.isAvailable('branch_products'));
  readonly branchProductsPaywalled = computed(() => this.isPaywalled('branch_products'));

  readonly reportsEnabled = computed(() => this.isEnabled('reports'));
  readonly reportsAvailable = computed(() => this.isAvailable('reports'));
  readonly reportsPaywalled = computed(() => this.isPaywalled('reports'));

  readonly multiBusinessTypeEnabled = computed(() => this.isEnabled('multi_business_type'));

  readonly deliveryEnabled = computed(() => this.isEnabled('delivery'));
  readonly deliveryAvailable = computed(() => this.isAvailable('delivery'));
  readonly deliveryPaywalled = computed(() => this.isPaywalled('delivery'));

  readonly reservationsEnabled = computed(() => this.isEnabled('reservations'));
  readonly reservationsAvailable = computed(() => this.isAvailable('reservations'));
  readonly reservationsPaywalled = computed(() => this.isPaywalled('reservations'));

  // Branch-to-branch transfer — sub-setting of the inventory feature, not a top-level flag.
  readonly inventoryTransferEnabled = computed(() => {
    if (!this.inventoryEnabled()) return false;
    return this.inventorySettings().transfer_enabled === true;
  });

  /**
   * Load all feature flags for the current organization
   */
  async loadFeatureFlags(): Promise<void> {
    const orgId = this.orgContext.effectiveOrganizationId();
    if (!orgId) {
      this._featureFlags.set([]);
      return;
    }

    this._loading.set(true);
    try {
      const { data, error } = await this.supabase.client
        .from('organization_feature_flags')
        .select('*')
        .eq('organization_id', orgId);

      if (error) throw error;

      this._featureFlags.set(data || []);
      this._loaded.set(true);
    } catch (error) {
      this.errorHandler.handleError(error, 'loadFeatureFlags');
      this._featureFlags.set([]);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Get settings for a specific feature
   */
  getSettings<T extends FeatureSettings>(featureCode: FeatureCode): T | null {
    const flags = this._featureFlags();
    const flag = flags.find((f) => f.feature_code === featureCode);
    return (flag?.settings as T) ?? null;
  }

  /**
   * Get a specific feature flag
   */
  getFeatureFlag(featureCode: FeatureCode): FeatureFlag | undefined {
    return this._featureFlags().find((f) => f.feature_code === featureCode);
  }

  /**
   * Enable a feature with settings
   */
  async enableFeature(featureCode: FeatureCode, settings?: FeatureSettings): Promise<FeatureFlag> {
    const orgId = this.orgContext.effectiveOrganizationId();
    if (!orgId) throw new Error('No organization selected');

    try {
      const existingFlag = this.getFeatureFlag(featureCode);

      if (existingFlag) {
        // Update existing flag
        const { data, error } = await this.supabase.client
          .from('organization_feature_flags')
          .update({
            is_enabled: true,
            settings: settings ?? existingFlag.settings,
            enabled_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingFlag.id)
          .select()
          .single();

        if (error) throw error;

        // Update local state
        this._featureFlags.update((flags) =>
          flags.map((f) => (f.id === existingFlag.id ? data : f)),
        );

        return data;
      } else {
        // Create new flag
        const defaultSettings = this.getDefaultSettings(featureCode);
        const { data, error } = await this.supabase.client
          .from('organization_feature_flags')
          .insert({
            organization_id: orgId,
            feature_code: featureCode,
            is_enabled: true,
            settings: settings ?? defaultSettings,
            enabled_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) throw error;

        // Update local state
        this._featureFlags.update((flags) => [...flags, data]);

        return data;
      }
    } catch (error) {
      throw this.errorHandler.handleError(error, 'enableFeature');
    }
  }

  /**
   * Disable a feature
   */
  async disableFeature(featureCode: FeatureCode): Promise<void> {
    const existingFlag = this.getFeatureFlag(featureCode);
    if (!existingFlag) {
      // A `defaultEnabled` feature reads as ON with no row, so returning early
      // would make the toggle a silent no-op that reverts on the next reload.
      // Write the explicit `false` instead. Opt-in features are already off.
      const entry = FEATURE_REGISTRY.find((f) => f.code === featureCode);
      if (entry?.defaultEnabled) await this.insertDisabledFlag(featureCode);
      return;
    }

    try {
      const { error } = await this.supabase.client
        .from('organization_feature_flags')
        .update({
          is_enabled: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingFlag.id);

      if (error) throw error;

      // Update local state
      this._featureFlags.update((flags) =>
        flags.map((f) => (f.id === existingFlag.id ? { ...f, is_enabled: false } : f)),
      );
    } catch (error) {
      throw this.errorHandler.handleError(error, 'disableFeature');
    }
  }

  /**
   * Create an explicit `is_enabled: false` row for a feature that has none.
   * Only reached for `defaultEnabled` features — see `disableFeature()`.
   */
  private async insertDisabledFlag(featureCode: FeatureCode): Promise<void> {
    const orgId = this.orgContext.effectiveOrganizationId();
    if (!orgId) throw new Error('No organization selected');

    try {
      const { data, error } = await this.supabase.client
        .from('organization_feature_flags')
        .insert({
          organization_id: orgId,
          feature_code: featureCode,
          is_enabled: false,
          settings: this.getDefaultSettings(featureCode),
        })
        .select()
        .single();

      if (error) throw error;

      this._featureFlags.update((flags) => [...flags, data]);
    } catch (error) {
      throw this.errorHandler.handleError(error, 'disableFeature');
    }
  }

  /**
   * Update feature settings
   */
  async updateFeatureSettings(
    featureCode: FeatureCode,
    settings: FeatureSettings,
  ): Promise<FeatureFlag> {
    const existingFlag = this.getFeatureFlag(featureCode);

    if (existingFlag) {
      try {
        const { data, error } = await this.supabase.client
          .from('organization_feature_flags')
          .update({
            settings,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingFlag.id)
          .select()
          .single();

        if (error) throw error;

        // Update local state
        this._featureFlags.update((flags) =>
          flags.map((f) => (f.id === existingFlag.id ? data : f)),
        );

        return data;
      } catch (error) {
        throw this.errorHandler.handleError(error, 'updateFeatureSettings');
      }
    } else {
      // Create new flag with settings (but disabled)
      return this.enableFeature(featureCode, settings);
    }
  }

  /**
   * Toggle feature on/off
   */
  async toggleFeature(featureCode: FeatureCode): Promise<boolean> {
    const isCurrentlyEnabled = this.isEnabled(featureCode);

    if (isCurrentlyEnabled) {
      await this.disableFeature(featureCode);
      return false;
    } else {
      await this.enableFeature(featureCode);
      return true;
    }
  }

  /**
   * Get default settings for a feature
   */
  private getDefaultSettings(featureCode: FeatureCode): FeatureSettings {
    switch (featureCode) {
      case FEATURE_CODES.INVENTORY:
        return DEFAULT_INVENTORY_SETTINGS;
      case FEATURE_CODES.BRANCH_PRODUCTS:
        return {};
      default:
        return {};
    }
  }

  /**
   * Clear loaded state (for org switch)
   */
  clearState(): void {
    this._featureFlags.set([]);
    this._loaded.set(false);
  }
}
